import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PauseCircle, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Pausing generation and AdSense-safe mode. Shared by News & Feeds and Blog:
// both save straight to /settings/generation, so either page shows the truth.
const BLOCKS = [
  ["none", "Running", "Articles and social posts are generated as usual."],
  ["articles", "Pause articles", "No blog articles are written. Social posts carry on (without an article link)."],
  ["social", "Pause social posts", "No social posts or engine runs. Articles carry on and wait for their post."],
  ["both", "Pause everything", "Nothing is generated and the AI is not called. New feed items wait."],
];

const chip = (on, tone = "indigo") =>
  `px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${on
    ? tone === "rose" ? "border-rose-400/60 bg-rose-500/15 text-rose-100"
      : tone === "emerald" ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-100"
        : "border-indigo-400/60 bg-indigo-500/15 text-indigo-100"
    : "border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"}`;

export const GenerationControls = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setC({
      generation_block: data.generation_block || "none",
      adsense_safe: !!data.adsense_safe,
      adsense_safe_level: data.adsense_safe_level || "standard",
      adsense_safe_scope: data.adsense_safe_scope || "articles",
    })).catch(() => {});
  }, []);

  const save = async (patch, key) => {
    setSaving(key);
    const before = c;
    setC({ ...c, ...patch });
    try {
      const { data } = await api.put("/settings/generation", patch);
      setC(data);
      if ("generation_block" in patch) {
        const b = BLOCKS.find(([k]) => k === data.generation_block);
        toast.success(b[0] === "none" ? "Generation is running" : `${b[1]} - the AI will not be called for it`);
      } else if ("adsense_safe" in patch) {
        toast.success(data.adsense_safe ? "AdSense-safe mode on - risky stories are skipped before writing" : "AdSense-safe mode off");
      }
    } catch (e) {
      setC(before);
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(""); }
  };

  if (!c) return null;
  const block = BLOCKS.find(([k]) => k === c.generation_block) || BLOCKS[0];
  const paused = c.generation_block !== "none";

  return (
    <div className={`glass rounded-2xl p-5 space-y-4 ${paused ? "ring-1 ring-rose-500/40" : ""}`} data-testid="generation-controls">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <PauseCircle className={`w-4 h-4 ${paused ? "text-rose-300" : "text-slate-400"}`} />
            <h3 className="font-heading font-bold text-slate-100 text-sm">Generation</h3>
            {saving === "block" && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {BLOCKS.map(([k, label]) => (
              <button key={k} data-testid={`block-${k}`} disabled={!!saving}
                onClick={() => k !== c.generation_block && save({ generation_block: k }, "block")}
                className={chip(c.generation_block === k, k === "none" ? "emerald" : "rose")}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500">{block[2]}</p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className={`w-4 h-4 ${c.adsense_safe ? "text-emerald-300" : "text-slate-400"}`} />
            <h3 className="font-heading font-bold text-slate-100 text-sm">AdSense-safe</h3>
            {saving === "adsense" && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            <button data-testid="adsense-toggle" disabled={!!saving}
              onClick={() => save({ adsense_safe: !c.adsense_safe }, "adsense")}
              className={`ml-auto relative w-10 h-5 rounded-full transition-colors ${c.adsense_safe ? "bg-emerald-500" : "bg-slate-700"}`}
              aria-label="AdSense-safe mode">
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${c.adsense_safe ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
          <div className={`flex flex-wrap gap-1.5 ${c.adsense_safe ? "" : "opacity-50 pointer-events-none"}`}>
            {[["standard", "Standard"], ["strict", "Strict"]].map(([k, l]) => (
              <button key={k} onClick={() => save({ adsense_safe_level: k }, "adsense")}
                className={chip(c.adsense_safe_level === k)}>{l}</button>
            ))}
            <span className="w-px bg-slate-800 mx-1" />
            {[["articles", "Articles only"], ["both", "Articles + social"]].map(([k, l]) => (
              <button key={k} onClick={() => save({ adsense_safe_scope: k }, "adsense")}
                className={chip(c.adsense_safe_scope === k)}>{l}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500">
            Stories about sexual content, sexual violence, gore, suicide or hard drugs
            {c.adsense_safe_level === "strict" ? " - and killings, shootings, weapons, gambling and racy gossip -" : ""}
            {" "}are skipped before anything is written, so they cost nothing. Skipped items show the reason under Recent articles.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GenerationControls;
